/**
 * Token payment configuration.
 * Reads from Firestore 'appSettings/tokenPayment'.
 * Two tokens are supported:
 *   - standard (CXBT by default) — used for all regular categories
 *   - reward token — used for items in the "Bonuses" category
 * Name / symbol / decimals are pulled from the token contracts themselves
 * (see utils/erc20.js) and stored here by the admin panel.
 */

const DEFAULTS = {
  // Standard payment token
  tokenName: 'CXBT',
  tokenFullName: '',
  tokenAddress: '',
  tokenNetwork: 'ethereum',
  tokenDecimals: 6,
  eurPerToken: 1,
  commissionPercent: 0,
  // Reward token ("Bonuses" category items are priced in it)
  rewardTokenName: '',
  rewardTokenFullName: '',
  rewardTokenAddress: '',
  rewardTokenNetwork: 'ethereum',
  rewardTokenDecimals: 18,
  rewardEurPerToken: 1,
};

// Shared with the admin token settings screen
export const DEFAULT_TOKEN_CONFIG = DEFAULTS;

export const TOKEN_KINDS = {
  STANDARD: 'standard',
  REWARD: 'reward',
};

let cachedConfig = null;
let listeners = [];

/**
 * Subscribe to token config changes from Firestore.
 * Returns an unsubscribe function.
 */
export function subscribeTokenConfig(cb) {
  listeners.push(cb);
  if (cachedConfig) cb(cachedConfig);
  return () => {
    listeners = listeners.filter((l) => l !== cb);
  };
}

/**
 * Load token config from Firestore (called once at app init).
 */
export async function loadTokenConfig(db) {
  try {
    const doc = await db.doc('appSettings/tokenPayment').get();
    cachedConfig = doc.exists ? { ...DEFAULTS, ...doc.data() } : { ...DEFAULTS };
  } catch {
    cachedConfig = { ...DEFAULTS };
  }
  listeners.forEach((l) => l(cachedConfig));
  return cachedConfig;
}

/**
 * Get current token config (cached).
 */
export function getTokenConfig() {
  return cachedConfig || { ...DEFAULTS };
}

/**
 * Per-kind view of the config: { name, fullName, address, network, decimals,
 * eurPerToken } for the standard or the reward token.
 * @param {string} kind - 'standard' | 'reward'
 */
export function getTokenConfigFor(kind) {
  const cfg = getTokenConfig();
  if (kind === TOKEN_KINDS.REWARD) {
    return {
      kind: TOKEN_KINDS.REWARD,
      name: cfg.rewardTokenName || 'REWARD',
      fullName: cfg.rewardTokenFullName || '',
      address: cfg.rewardTokenAddress,
      network: cfg.rewardTokenNetwork || 'ethereum',
      decimals: cfg.rewardTokenDecimals ?? 18,
      eurPerToken: cfg.rewardEurPerToken ?? 1,
      commissionPercent: cfg.commissionPercent ?? 0,
    };
  }
  return {
    kind: TOKEN_KINDS.STANDARD,
    name: cfg.tokenName || 'CXBT',
    fullName: cfg.tokenFullName || '',
    address: cfg.tokenAddress,
    network: cfg.tokenNetwork || 'ethereum',
    decimals: cfg.tokenDecimals ?? 6,
    eurPerToken: cfg.eurPerToken ?? 1,
    commissionPercent: cfg.commissionPercent ?? 0,
  };
}

/**
 * Items in the "Bonuses" category are sold for the reward token.
 * @param {string} categoryName
 */
export function isRewardCategory(categoryName) {
  return String(categoryName || '').trim().toLowerCase() === 'bonuses';
}

/**
 * Which token a given product is priced in.
 * Prefers the stored `paymentToken` field; falls back to the category check
 * for products created before the field existed.
 * @param {Object} product - product doc (or { category, paymentToken })
 * @returns {'standard' | 'reward'}
 */
export function tokenKindForProduct(product) {
  if (product?.paymentToken === TOKEN_KINDS.REWARD) return TOKEN_KINDS.REWARD;
  if (product?.paymentToken === TOKEN_KINDS.STANDARD) return TOKEN_KINDS.STANDARD;
  return isRewardCategory(product?.category)
    ? TOKEN_KINDS.REWARD
    : TOKEN_KINDS.STANDARD;
}

/**
 * Convert EUR amount to token amount (base units).
 * @param {number} eurAmount
 * @param {Object} config - token config (full config or per-kind view)
 * @returns {number} token base units (integer)
 */
export function eurToTokens(eurAmount, config) {
  const cfg = config || getTokenConfig();
  const tokens = (Number(eurAmount) || 0) / (cfg.eurPerToken || 1);
  const multiplier = Math.pow(10, cfg.tokenDecimals || 6);
  return Math.round(tokens * multiplier);
}

/**
 * Format a EUR-amount price for display in tokens.
 * Since all product prices in Firestore are stored as EUR-equivalent numbers,
 * this converts them to the token display: "1,250.5 CXBT".
 * Handles decimal input correctly by using parseFloat, not base-unit integers.
 * @param {number} eurAmount - price in EUR equivalent
 * @param {Object} config
 * @returns {string} human-readable token amount (no symbol)
 */
export function formatTokenAmount(eurAmount, config) {
  const cfg = config || getTokenConfig();
  const decimals = cfg.tokenDecimals || 6;
  const tokens = (Number(eurAmount) || 0) / (cfg.eurPerToken || 1);
  // Format with up to 'decimals' decimal places, trim trailing zeros
  return tokens.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.min(decimals, 6),
  });
}

/**
 * Parse user input into token base units.
 * Handles both dot and comma decimal separators.
 * @param {string} input
 * @param {Object} config
 * @returns {number} token base units (integer), or NaN if invalid
 */
export function parseTokenInput(input, config) {
  const cfg = config || getTokenConfig();
  const decimals = cfg.tokenDecimals || 6;
  const multiplier = Math.pow(10, decimals);
  const cleaned = String(input).replace(/,/g, '.').replace(/[^0-9.]/g, '');
  const value = parseFloat(cleaned);
  if (isNaN(value)) return NaN;
  return Math.round(value * multiplier);
}

/**
 * Calculate commission for a given token amount.
 * @param {number} tokenBaseUnits
 * @param {Object} config
 * @returns {number} commission in base units
 */
export function calculateCommission(tokenBaseUnits, config) {
  const cfg = config || getTokenConfig();
  const pct = Number(cfg.commissionPercent) || 0;
  return Math.round((Number(tokenBaseUnits) || 0) * pct / 100);
}
